import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, useOutsideClickEffect } from '@saviynt/common';
import {
  Button,
  Checkbox,
  Icon,
  Link,
  MobileDrawer,
  MobileDrawerItem,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  appliedSaviActionFilter,
  appliedSaviSmartFilter,
  clearSaviActionsFilterAction,
  clearSmartFiltersAction,
  setAppliedSaviActionKeys,
  setIsSaviApplied,
  triggerFilterChange,
  updateCertSmartFilter,
} from '../../../../redux/certdetails/action';
import { ITEMS_API_OFFSET } from '../../../../utils/constant';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import SmartFilterButtonSelect from '../components/SmartFilterButtonSelect';
import { invertedMessages } from '../messages';

import './SaviActionsFilters.css';

function SaviActionsFilterItem({
  id,
  label,
  onClick,
  isFilterSelected,
  isMobileResponsivenessEnabled,
}) {
  const isChecked = isFilterSelected[id] || false;

  const handleOptionClick = (event) => {
    // prevent the default behavior and event bubbling.
    event.preventDefault();
    event.stopPropagation();

    onClick(id, !isChecked);
  };

  if (isMobileResponsivenessEnabled)
    return (
      <MobileDrawerItem
        key={id}
        option={{ label, isChecked, value: id, isMulti: true }}
        onClick={() => onClick(id, !isChecked)}
      />
    );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <li
      key={1}
      onClick={handleOptionClick}
      className='SaviActionsFilter-menuItem'
      role='option'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleOptionClick(e);
        }
      }}
      aria-selected={isFilterSelected && isFilterSelected[id]}>
      {/* TODO: remove MenuItemMulti-checkbox classname per the standards, al theses should be using NEO components anyway */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div className='MenuItemMulti-checkbox'>
        <Checkbox
          id='savi-action-filters-checkbox'
          kind='base'
          tabIndex={-1}
          isChecked={isChecked}
        />
      </div>
      <Typography kind='body2' className='SaviActionsFilter-menuItemText'>
        {label}
      </Typography>
    </li>
  );
}

SaviActionsFilterItem.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isFilterSelected: PropTypes.shape({}),
  isMobileResponsivenessEnabled: PropTypes.bool,
};

SaviActionsFilterItem.defaultProps = {
  isFilterSelected: {},
  isMobileResponsivenessEnabled: false,
};

function SaviActionsFilters({
  setIsSaviActionsApplied,
  isSmartFilterApplied,
  isMobileDrawerOpen,
  setIsMobileDrawerOpen,
  isMobileResponsivenessEnabled,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterSelected, setIsFilterSelected] = useState({});
  //   const [selectedFactor, setSelectedFactor] = useState('');
  //   const [showSmartFilterLoader, setShowSmartFilterLoader] = useState(false);
  //   const { messages } = useSelector((state) => state?.user);

  const {
    global: { certKey },
    saviCompanion: { clearSaviActionsFilter },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const saviFilterWrapperRef = useRef(null);
  const saviFilterDropdownRef = useRef(null);

  const SAVI_ACTIONS_FILTER_OPTIONS = [
    {
      key: 'SAVI_APPROVED',
      label:
        messages.CERTS_SAVI_APPROVED ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVED')),
    },
    {
      key: 'SAVI_REJECTED',
      label:
        messages.CERTS_SAVI_REJECTED ||
        localize(intl, getMessageKey('CERTS_SAVI_REJECTED')),
    },
    //   {
    //     key: 'SAVI_OVERWRITTEN',
    //     label: 'Savi overwritten actions',
    //   },
    //   {
    //     key: 'SAVI_REC_IGNORED',
    //     label: 'Savi actions overwritten',
    //   },
  ];

  useOutsideClickEffect(saviFilterWrapperRef, () => setIsOpen(false));

  const isAnyFilterSelected = Object.keys(isFilterSelected).length > 0;

  const clearFilter = () => {
    if (isMobileResponsivenessEnabled) {
      setIsMobileDrawerOpen(false);
    }

    if (Object.keys(isFilterSelected).length > 0) {
      dispatch(setAppliedSaviActionKeys([]));
      dispatch(triggerFilterChange());
      setIsFilterSelected({});
    }
  };

  useEffect(() => {
    setIsSaviActionsApplied(isAnyFilterSelected);
    dispatch(setIsSaviApplied(isAnyFilterSelected));
  }, [isAnyFilterSelected]);

  useEffect(() => {
    if (clearSaviActionsFilter) {
      clearFilter();
      dispatch(clearSaviActionsFilterAction(false));
    }
  }, [clearSaviActionsFilter]);

  useEffect(() => {
    // This function is used to get applied savi action filters for exporting
    const findAppliedActionFilterForExport = () => {
      const appliedFilters = Object.keys(isFilterSelected).map(
        (key) =>
          SAVI_ACTIONS_FILTER_OPTIONS.find((item) => item.key === key)?.label
      );

      dispatch(appliedSaviActionFilter(appliedFilters));
    };

    findAppliedActionFilterForExport();
  }, [isFilterSelected]);

  const filterSaviActions = (id, isChecked) => {
    const newKeys = isChecked
      ? [...Object.keys(isFilterSelected), id]
      : Object.keys(isFilterSelected).filter((k) => k !== id);

    dispatch(setAppliedSaviActionKeys(newKeys));
    dispatch(triggerFilterChange());
  };

  const updateSmartFilter = (id, isChecked) => {
    if (isChecked) {
      setIsFilterSelected({
        ...isFilterSelected,
        [id]: true,
      });
    } else {
      const _isFilterSelected = { ...isFilterSelected };

      delete _isFilterSelected[id];
      setIsFilterSelected(_isFilterSelected);
    }

    const promiseToComplete = isSmartFilterApplied
      ? [
          dispatch(clearSmartFiltersAction(true)),
          dispatch(
            updateCertSmartFilter(
              {
                certkey: certKey,
                clearFilter: 'true',
              },
              {
                ITEMS_API_OFFSET,
                dontRefreshItems: true,
              }
            )
          ),
          dispatch(appliedSaviSmartFilter('')),
        ]
      : [];

    if (promiseToComplete.length > 0) {
      Promise.all(promiseToComplete).then(() => {
        filterSaviActions(id, isChecked);
      });
    } else {
      filterSaviActions(id, isChecked);
    }
  };

  useEffect(() => {
    if (!saviFilterDropdownRef.current) return;

    const updateFocusableElements = () => {
      const focusableElements = saviFilterDropdownRef.current
        .querySelectorAll(`.SaviActionsFilter-dropdown button, 
           .SaviActionsFilter-dropdown [href], 
           .SaviActionsFilter-dropdown input, 
           .SaviActionsFilter-dropdown select, 
           .SaviActionsFilter-dropdown textarea, 
           .SaviActionsFilter-dropdown [tabindex]:not([tabindex="-1"]),
           .SaviActionsFilter-dropdown li`);

      focusableElements.forEach((el) => {
        if (!isOpen) {
          // Store previous tabindex if not already stored
          if (!el.hasAttribute('data-prev-tabindex')) {
            el.setAttribute(
              'data-prev-tabindex',
              el.getAttribute('tabindex') || '0'
            );
          }

          // eslint-disable-next-line no-param-reassign
          el.tabIndex = -1;
        } else {
          const prevTabIndex = el.getAttribute('data-prev-tabindex');

          if (prevTabIndex !== null) {
            // eslint-disable-next-line no-param-reassign
            el.tabIndex = parseInt(prevTabIndex, 10);
            el.removeAttribute('data-prev-tabindex');
          } else {
            el.removeAttribute('tabindex');
          }
        }
      });
    };

    updateFocusableElements();

    // MutationObserver to detect when elements become visible
    const observer = new MutationObserver(() => {
      updateFocusableElements();
    });

    observer.observe(saviFilterDropdownRef.current, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [isOpen, saviFilterDropdownRef]);

  if (isMobileResponsivenessEnabled) {
    return (
      <>
        <div className='SaviFilter-dropdown-displayFlex'>
          {SAVI_ACTIONS_FILTER_OPTIONS.map(({ key, label }) => (
            <SaviActionsFilterItem
              key={key}
              id={key}
              label={label}
              onClick={updateSmartFilter}
              isFilterSelected={isFilterSelected}
              isMobileResponsivenessEnabled
            />
          ))}
        </div>
        <MobileDrawer
          isOpen={isMobileDrawerOpen}
          setIsOpen={setIsMobileDrawerOpen}
          isSwipeEnabled
          content={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={clearFilter}
              isDisabled={!isAnyFilterSelected}
              className='AdvancedFilters-filterDrawerClearAndRemoveBtns'>
              {l('Clear Filter')}
            </Button>
          }
        />
      </>
    );
  }

  // if updating `SaviActionsFilter-wrapper` class, `constant.js` on Line -> 25
  return (
    <div className='SaviActionsFilter-wrapper' ref={saviFilterWrapperRef}>
      <SmartFilterButtonSelect
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        label={
          messages.CERTS_SAVI_FILTERS ||
          localize(intl, getMessageKey('CERTS_SAVI_FILTERS'))
        }
        showBadge={isAnyFilterSelected}
        prefixIcon={<Icon kind='SaviCompanionGradient' size='medium' />}
      />
      <div
        ref={saviFilterDropdownRef}
        className={classnames(
          'SaviActionsFilter-dropdown',
          isOpen && 'SaviActionsFilter-dropdown--isVisible'
        )}>
        <div className='SmartFilter-dropdown-displayFlex'>
          {SAVI_ACTIONS_FILTER_OPTIONS.map(({ key, label }) => (
            <SaviActionsFilterItem
              key={key}
              id={key}
              label={label}
              onClick={updateSmartFilter}
              isFilterSelected={isFilterSelected}
            />
          ))}
        </div>
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          className='SaviActionsFilter-footer'
          role='button'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              clearFilter();
            }
          }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            text={l('Clear')}
            kind='button'
            onClick={clearFilter}
            isDisabled={!isAnyFilterSelected}
            size='small'
          />
        </div>
      </div>
    </div>
  );
}

SaviActionsFilters.propTypes = {
  setIsSaviActionsApplied: PropTypes.func.isRequired,
  isSmartFilterApplied: PropTypes.bool.isRequired,
  isMobileDrawerOpen: PropTypes.bool,
  setIsMobileDrawerOpen: PropTypes.func,
  isMobileResponsivenessEnabled: PropTypes.bool,
};

SaviActionsFilters.defaultProps = {
  isMobileDrawerOpen: false,
  setIsMobileDrawerOpen: () => {},
  isMobileResponsivenessEnabled: false,
};

export default SaviActionsFilters;
